import { useFragment } from "@/generated";
import { AddSlideDocument, SlideFieldsFragmentDoc, type SlideInput } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for adding new slides with Apollo cache updates.
 *
 * @example
 * const { addSlide, loading } = useAddSlide();
 * const newSlides = await addSlide({
 *   name: "New Slide",
 *   lessonId: "lesson123",
 *   type: "word",
 *   elements: []
 * });
 */
export function useAddSlide() {
  const [addSlideMutation, { loading }] = useMutation(AddSlideDocument, {
    update(cache, { data }) {
      if (!data?.addSlide) return;

      const slides = data.addSlide.map((slide) => useFragment(SlideFieldsFragmentDoc, slide));

      // A subslide belongs to its parent's `children`, never to the lesson list.
      if (slides.some((slide) => slide.parentSlideId)) {
        cache.batch({
          update(cache) {
            for (const slide of slides) {
              if (!slide.parentSlideId) continue;

              const id = cache.identify(slide);
              const parentId = cache.identify({ __typename: "Slide", id: slide.parentSlideId });
              if (!id || !parentId) continue;

              cache.writeFragment({ id, fragment: SlideFieldsFragmentDoc, data: slide });
              cache.modify({
                id: parentId,
                fields: {
                  children(existingData = []) {
                    return [...existingData, { __ref: id }];
                  },
                },
              });
            }
          },
        });
        return;
      }

      // Group slides by lessonId to handle multiple slides for the same lesson
      const slidesByLessonIdMap = new Map<string, typeof slides>();

      for (const slide of slides) {
        const key = slide.lessonId.toString();
        const existing = slidesByLessonIdMap.get(key) ?? [];
        slidesByLessonIdMap.set(key, [...existing, slide]);
      }

      cache.batch({
        update(cache) {
          for (const [lessonId, lessonSlides] of slidesByLessonIdMap.entries()) {
            try {
              // Write all slides to cache first
              const newRefs = lessonSlides
                .map((slide) => {
                  const id = cache.identify(slide);
                  if (id) {
                    cache.writeFragment({
                      id,
                      fragment: SlideFieldsFragmentDoc,
                      data: slide,
                    });
                    return { __ref: id };
                  }
                  return null;
                })
                .filter((ref) => ref !== null);

              cache.modify({
                id: "ROOT_QUERY",
                fields: {
                  slidesByLessonId(existingData, { storeFieldName }) {
                    const key = `"lessonId":"${lessonId}"`;
                    const isTrashKey = '"isTrash":false';

                    if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                      return existingData;

                    if (!existingData?.items) return existingData;

                    return {
                      ...existingData,
                      items: [...newRefs, ...existingData.items],
                      total: (existingData.total || 0) + newRefs.length,
                    };
                  },
                },
              });
            } catch (error) {
              console.error("Add slide cache update failed:", error);
            }
          }
        },
      });
    },
  });

  const addSlide = async (input: SlideInput) => {
    return withToast(
      async () => {
        const result = await addSlideMutation({
          variables: { input },
        });

        const maskedSlides = result.data?.addSlide ?? [];
        const newSlides = maskedSlides.map((slide) => useFragment(SlideFieldsFragmentDoc, slide));

        return newSlides;
      },
      {
        loading: "Creating slide...",
        success: `Successfully created ${input.parentSlideId ? "subslide" : "slide"} "${input.name}"`,
        error: "Failed to create slides. Please try again.",
      },
      "addSlide"
    );
  };

  return {
    addSlide,
    loading,
  };
}
